import { useState, type ReactNode } from 'react';
import { Tabs, type TabItem } from '../components/Tabs.tsx';
import type { Question } from '../lib/types.ts';
import type { QuestionnaireGroup } from '../lib/entityQuestionnaireTabs.ts';

// Synthetic key for the leading info tab; underscores keep it clear of any real
// questionnaire UUID.
const INFO_TAB = '__info__';

// Tabbed detail layout shared by the driver and asset pages: an info tab first,
// then one tab per applicable questionnaire whose category sections render via
// the caller's renderQuestion. Mirrors the company detail tab strip.
export function EntityQuestionnaireTabs({
  infoLabel,
  infoPanel,
  groups,
  tabBadge,
  renderQuestion,
  ariaLabel,
}: {
  infoLabel: string;
  infoPanel: ReactNode;
  groups: QuestionnaireGroup[];
  tabBadge: (group: QuestionnaireGroup) => number;
  renderQuestion: (question: Question) => ReactNode;
  ariaLabel: string;
}) {
  const tabItems: TabItem[] = [
    { key: INFO_TAB, label: infoLabel },
    ...groups.map((g) => ({
      key: g.questionnaire.id,
      label: g.questionnaire.name,
      badge: tabBadge(g),
    })),
  ];

  const [active, setActive] = useState<string>(INFO_TAB);
  const activeKey = tabItems.some((t) => t.key === active) ? active : INFO_TAB;
  const activeGroup = groups.find((g) => g.questionnaire.id === activeKey);

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
      <Tabs
        tabs={tabItems}
        active={activeKey}
        onSelect={setActive}
        ariaLabel={ariaLabel}
        className="px-2 pt-2"
      />
      <div className="p-6">
        {activeGroup ? (
          <div
            role="tabpanel"
            id={`tabpanel-${activeGroup.questionnaire.id}`}
            aria-labelledby={`tab-${activeGroup.questionnaire.id}`}
          >
            <h2 className="mb-5 text-xl font-semibold text-slate-900">
              {activeGroup.questionnaire.name}
            </h2>
            <div className="space-y-4">
              {activeGroup.sections.map((s) => (
                <section
                  key={s.category.id}
                  className="overflow-hidden rounded-lg border border-slate-200"
                >
                  <h3 className="border-b border-slate-200 bg-slate-50 px-4 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">
                    {s.category.name}
                  </h3>
                  <div className="divide-y divide-slate-200 px-4">
                    {s.questions.map((q) => (
                      <div key={q.id}>{renderQuestion(q)}</div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        ) : (
          <div role="tabpanel" id={`tabpanel-${INFO_TAB}`} aria-labelledby={`tab-${INFO_TAB}`}>
            {infoPanel}
          </div>
        )}
      </div>
    </div>
  );
}
